import { Link } from "@tanstack/react-router";
import {
  BellRing,
  Copy,
  Package,
  Phone,
  Plus,
  ShoppingBag,
  UserIcon,
} from "@/components/icons";
import { Badge, Button } from "@/components/kit";
import { cn } from "@/lib/utils";
import type { RestockWaitlistListItemDTO } from "@/modules/restock-waitlist/domain/types";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";
import {
  getWaitlistStatusMeta,
  waitlistStatusLabel,
} from "./waitlist-status";

type WaitlistListCardProps = {
  row: RestockWaitlistListItemDTO;
  canManageWaitlist: boolean;
  canCreateOrder: boolean;
  notifyPending?: boolean;
  onOpen: () => void;
  onCopy: () => void;
  onNotify: () => void;
  onCreateOrder: () => void;
};

function StatChip({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: string;
  tone?: "default" | "ready" | "warn";
}) {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-0.5 rounded-xl px-3 py-2",
        tone === "ready" && "bg-secondary/12 text-secondary",
        tone === "warn" && "bg-destructive/8 text-destructive",
        tone === "default" && "bg-muted/50 text-foreground",
      )}
    >
      <span className="text-[11px] text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      <span className="truncate font-medium text-sm tabular-nums">{value}</span>
    </div>
  );
}

export function WaitlistListCard({
  row,
  canManageWaitlist,
  canCreateOrder,
  notifyPending = false,
  onOpen,
  onCopy,
  onNotify,
  onCreateOrder,
}: WaitlistListCardProps) {
  const meta = getWaitlistStatusMeta(row.status);
  const hasLinkedOrder = Boolean(row.convertedOrderId);
  const canCopy = row.status === "ready" || row.status === "notified";
  const canNotify = row.status === "ready";
  const canCreateOrderFromRow =
    canCreateOrder &&
    !hasLinkedOrder &&
    (row.status === "ready" || row.status === "notified");
  const showActions =
    hasLinkedOrder ||
    (canManageWaitlist && (canNotify || canCopy || canCreateOrderFromRow));

  const holdExpiresMs =
    row.holdExpiresAt != null
      ? new Date(row.holdExpiresAt).getTime()
      : null;
  const holdSoon =
    holdExpiresMs != null && holdExpiresMs - Date.now() < 24 * 60 * 60 * 1000;

  return (
    <article className="brand-card overflow-hidden p-0 transition-colors hover:border-secondary/45">
      <button
        type="button"
        className="w-full text-left"
        onClick={onOpen}
      >
        <div className="bg-linear-to-br from-secondary/12 via-primary/6 to-background px-4 py-4 sm:px-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-display text-lg text-secondary tabular-nums">
                {row.waitNumber}
              </p>
              <p className="mt-0.5 text-muted-foreground text-xs">
                ບັນທຶກ {formatDateLocal(row.createdAt)}
              </p>
            </div>
            <Badge
              variant={meta.variant}
              className={cn("shrink-0 rounded-full", meta.className)}
            >
              {waitlistStatusLabel(row.status)}
            </Badge>
          </div>

          <div className="mt-4 flex items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-2xl bg-background/80 text-secondary shadow-sm">
              <UserIcon className="size-4" />
            </div>
            <div className="min-w-0">
              <p className="truncate font-medium">{row.customerName}</p>
              {row.customerPhone ? (
                <p className="mt-0.5 flex items-center gap-1.5 text-muted-foreground text-sm">
                  <Phone className="size-3.5 shrink-0" />
                  <span className="truncate">{row.customerPhone}</span>
                </p>
              ) : null}
            </div>
          </div>

          <div className="mt-3 flex items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-2xl bg-background/70 text-muted-foreground shadow-sm">
              <Package className="size-4" />
            </div>
            <div className="min-w-0">
              <p className="truncate font-medium text-sm">{row.summaryLabel}</p>
              {row.lineCount > 1 ? (
                <ul className="mt-1 space-y-0.5 text-muted-foreground text-xs">
                  {row.lines.slice(0, 3).map((line) => (
                    <li key={line.id} className="truncate">
                      {line.productName} · {line.variantName} ×{" "}
                      {line.quantityRequested}
                    </li>
                  ))}
                  {row.lineCount > 3 ? (
                    <li>+ {row.lineCount - 3} ລາຍການ</li>
                  ) : null}
                </ul>
              ) : row.lines[0] ? (
                <p className="mt-0.5 text-muted-foreground text-sm">
                  {row.lines[0].variantName} ·{" "}
                  <span className="font-medium text-foreground">
                    × {row.lines[0].quantityRequested}
                  </span>
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 border-secondary/10 border-t px-4 py-3 sm:grid-cols-3 sm:px-5">
          <StatChip
            label="ສະຕັອກ"
            value={
              row.lines.length === 1
                ? String(row.lines[0]?.stockOnHand ?? 0)
                : `${row.lines.filter((line) => line.stockOnHand >= line.quantityRequested).length}/${row.lineCount} ພ້ອມ`
            }
            tone={
              row.lines.every(
                (line) => line.stockOnHand >= line.quantityRequested,
              )
                ? "ready"
                : "default"
            }
          />
          {row.lines.length === 1 && row.lines[0]?.queuePosition != null ? (
            <StatChip label="ຄິວ" value={`#${row.lines[0].queuePosition}`} />
          ) : row.lineCount > 1 ? (
            <StatChip label="ລາຍການ" value={String(row.lineCount)} />
          ) : (
            <StatChip label="ຄິວ" value="—" />
          )}
          {row.holdExpiresAt ? (
            <StatChip
              label="hold"
              value={formatDateTimeLocal(row.holdExpiresAt)}
              tone={holdSoon ? "warn" : "default"}
            />
          ) : (
            <StatChip label="hold" value="—" />
          )}
        </div>

        {row.staffNote ? (
          <p className="border-secondary/10 border-t px-4 py-2 text-muted-foreground text-sm sm:px-5">
            {row.staffNote}
          </p>
        ) : null}
      </button>

      {showActions ? (
        <div className="flex flex-wrap gap-2 border-secondary/15 border-t bg-muted/20 px-4 py-3 sm:px-5">
          {canNotify ? (
            <Button
              type="button"
              size="sm"
              className="rounded-full"
              disabled={notifyPending}
              onClick={(event) => {
                event.stopPropagation();
                onNotify();
              }}
            >
              <BellRing data-icon="inline-start" />
              ແຈ້ງແລ້ວ
            </Button>
          ) : null}
          {canCopy ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-full border-secondary/30"
              onClick={(event) => {
                event.stopPropagation();
                onCopy();
              }}
            >
              <Copy data-icon="inline-start" />
              Copy ຂໍ້ຄວາມ
            </Button>
          ) : null}
          {canCreateOrderFromRow ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="rounded-full"
              onClick={(event) => {
                event.stopPropagation();
                onCreateOrder();
              }}
            >
              <Plus data-icon="inline-start" />
              ສ້າງອໍເດີ້
            </Button>
          ) : hasLinkedOrder && row.convertedOrderId ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="rounded-full"
              asChild
            >
              <Link
                to="/app/orders/$id"
                params={{ id: row.convertedOrderId }}
                onClick={(event) => event.stopPropagation()}
              >
                <ShoppingBag data-icon="inline-start" />
                {row.convertedOrderNumber
                  ? row.convertedOrderNumber
                  : "ເບິ່ງອໍເດີ້"}
              </Link>
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="ml-auto rounded-full text-muted-foreground"
            onClick={(event) => {
              event.stopPropagation();
              onOpen();
            }}
          >
            <ShoppingBag data-icon="inline-start" />
            ລາຍລະອຽດ
          </Button>
        </div>
      ) : null}
    </article>
  );
}
